<template>
  <div
    ref="auditElement"
    v-resize="updateCardWidth"
    class="white"
  >
    <v-btn
      v-if="$vuetify.breakpoint.xs"
      fixed
      right
      bottom
      outline
      depressed
      class="text-xs-center sz_violet--text text-capitalize bg_white mobile-audit-btn"
      color="white"
      @click="toggleAuditCard()"
    >
      {{ $t('buttons.audit') }}
    </v-btn>
    <v-card
      v-show="auditCardFlag"
      class="fixed-bottom audit"
      :width="cardWidth"
      :style="centeredStyles"
    >
      <v-card-title
        ref="cardTitle"
        @click="toggleCard()"
      >
        <v-layout justify-space-between>
          <v-flex class="shrink">
            <h3
              class="display-2_dark"
              :class="[{ 'display-1': xsOnly }, { 'display-2': !xsOnly }]"
            >
              {{ $t('audit.block_title') }}
            </h3>
          </v-flex>
          <v-spacer />
          <v-flex class="shrink">
            <v-icon
              v-if="$vuetify.breakpoint.xs"
              size="26"
              color="sz_violet"
            >
              {{ toggleCardIcon }}
            </v-icon>
            <a
              v-else
              class="f-16 d-inline-flex"
              @click.prevent
            >
              {{ toggleCardText }}
              <v-icon
                size="26"
                color="sz_violet"
              >{{ toggleCardIcon }}</v-icon>
            </a>
          </v-flex>
        </v-layout>
      </v-card-title>
      <v-card-text
        ref="cardContent"
        class="scrolled"
        :style="[{ 'max-height': cardContentMaxHeight + 'px', display: toggleCardVisible }]"
      >
        <div ref="innerCardContent">
          <div ref="cardComment">
            <v-layout wrap>
              <v-flex xs12>
                <p
                  class="hide mb-1"
                  :style="{ height: instructionHeight }"
                >
                  {{ auditInstruction }}
                  <a
                    :href="auditGuide"
                    target="_blank"
                    class="small"
                  >{{
                    $t('gift_giving_report.instruction.link')
                  }}</a>
                </p>
              </v-flex>
              <v-flex
                v-if="showInstructionButton"
                xs12
                class="text-xs-center"
                @click="toggleInstruction()"
              >
                <a class="link_iconed small">{{ toggleInstructionTitle }}
                  <v-icon color="sz_violet">{{ toggleInstructionIcon }}</v-icon>
                </a>
              </v-flex>
            </v-layout>
            <div
              v-if="canAddComment && isCurrentEdition"
              class="relative"
            >
              <v-textarea
                ref="commentField"
                v-model="comment.content"
                v-validate="validationRules.get('auditComment')"
                :placeholder="$t('placeholders.add_text')"
                :error-messages="errors.collect('auditComment')"
                :data-vv-as="' '"
                :data-vv-name="'auditComment'"
              />
              <v-layout
                justify-center
                class="mt-2"
              >
                <v-btn
                  color="sz_violet"
                  :loading="commentLoader"
                  :disabled="commentLoader || isCommentInvalid"
                  @click="sendComment()"
                >
                  {{ $t('buttons.write') }}
                </v-btn>
                <v-snackbar
                  v-model="showCommentConfirmation"
                  absolute
                  :timeout="5000"
                  top
                  class="confirmation"
                  style="bottom: 0"
                >
                  <p class="my-2 text-xs-center green--text">
                    <v-icon color="green">
                      check
                    </v-icon>
                    {{ $t('audit.send_comment_confirmation') }}
                  </p>
                </v-snackbar>
              </v-layout>
            </div>
          </div>
          <v-layout
            wrap
            class="mt-3"
            column
          >
            <v-flex
              v-if="commentsList == null"
              xs12
            >
              <v-progress-linear
                color="sz_violet"
                height="3"
                :indeterminate="true"
              />
            </v-flex>
            <v-flex
              v-for="(comment, index) in commentsList"
              v-else
              :key="index"
              xs12
              class="comment-left mb-3"
            >
              <v-layout wrap>
                <v-flex
                  xs12
                  class="f-16 mb-0"
                >
                  <span class="font-weight-bold">
                    {{ comment.user.full_name }}
                  </span>
                  - <BaseEmail :email="comment?.user?.email" />,
                  <PhoneNumber :number="comment?.user?.phone" />
                </v-flex>
                <v-flex
                  xs12
                  class="f-14 mb-1"
                >
                  {{ comment.created_at | date }} {{ comment.created_at | time }}
                </v-flex>
                <v-flex
                  xs12
                  class="f-16 wrap-lines"
                >
                  {{ comment.content.trim() }}
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import validationRules from '@/constants/validationRules';
import { mapFields } from 'vuex-map-fields';
import filters from '@/constants/filters';

import links from '@/constants/links';
import permissions from '@/constants/permissions';
import statusesId from '@/constants/statusesId';

import BaseEmail from '@/components/widgets/BaseEmail.vue';
import PhoneNumber from '@/components/widgets/PhoneNumber.vue';

export default {
    name: 'FamilyAudit',
    components: {
        BaseEmail,
        PhoneNumber
    },
    filters: { ...filters },
    props: {
        canAddComment: {
            type: Boolean,
            default: false
        },
        centered: {
            type: Boolean,
            default: false
        },
        familyId: {
            required: true
        },
        sheetType: {
            required: true,
            type: String
        },
        commentLevel: {
            required: true,
            type: String
        },
        hasPermissionsToAddComment: {
            required: true,
            type: Boolean
        }
    },
    data () {
        return {
            validationRules,
            links,
            statusesId,
            toggleCardFlag: false,
            permissions,
            cardContentMaxHeight: 265,
            auditCardFlag: true,
            commentLoader: false,
            cardWidth: 0,
            showCommentConfirmation: false,
            hasPermissionsForNationalGiftGivingReportCorrecting: false,
            hasPermissionsForNationalCorrecting: false,
            instructionVisibility: null
        };
    },
    watch: {
        sheetAuditComments: {
            handler (val) {
                this.$store.dispatch('audit/getAuditComments', {
                    comments: val
                });
            },
            immediate: true,
            deep: true
        },
        xsOnly (val) {
            if (val === false) {
                this.auditCardFlag = true;
                this.$nextTick(() => {
                    this.updateAuditPadding();
                });
            } else {
                this.toggleAuditCard();
            }
        },
        getAuditExpand: {
            handler (val) {
                this.toggleCardFlag = val;
            },
            immediate: true
        },
        hasNationalPermission: {
            handler (val) {
                if (!this.isFamilyInAreaAudit && !this.isGiftGivingReportInAreaAudit) {
                    this.instructionVisibility = !val;
                }
            },
            immediate: true
        }
    },
    computed: {
        ...mapFields('audit', ['comment', 'commentsList']),
        getAuditExpand () {
            return this.$store.getters['audit/getAuditExpand'];
        },
        isGiftGivingReportAudit () {
            return this.sheetType === 'gift_giving_report';
        },
        cardMaxHeight () {
            return window.innerHeight / 2;
        },
        centeredStyles () {
            if (this.centered) {
                return {
                    left: '50%',
                    marginLeft: '-' + this.cardWidth / 2 + 'px'
                };
            }
            return false;
        },
        sheetAuditComments () {
            return this.$store.getters['sheet/getSheetAuditComments'];
        },
        family () {
            return this.$store.getters['family/getFamily'];
        },
        isFamilyInAreaAudit () {
            return [
                this.statusesId.FAMILY_VERIFIED_AREA_REACH,
                this.statusesId.SEND_BACK_AREA_REACH
            ].includes(this.familyStatusId);
        },
        isGiftGivingReportInAreaAudit () {
            return [
                this.statusesId.GIFT_GIVING_REPORT_AREA_REACH,
                this.statusesId.GIFT_GIVING_REPORT_BACK_AREA_REACH
            ].includes(this.familyStatusId);
        },
        auditInstruction () {
            if (this.isGiftGivingReportAudit) {
                if (this.hasPermissionsToAddComment) {
                    if (this.isGiftGivingReportInAreaAudit) {
                        return this.$t('audit.gift_giving_report_leader_instruction');
                    }
                    return this.$t('audit.gift_giving_report_so_instruction');
                }
                return this.$t('audit.gift_giving_report_volunteer_instruction');
            } else {
                if (this.hasPermissionsToAddComment) {
                    if (this.isFamilyInAreaAudit) {
                        return this.$t('audit.leader_instruction');
                    }
                    return this.$t('audit.so_instruction');
                }
                return this.$t('audit.volunteer_instruction');
            }
        },
        auditGuide () {
            if (this.isGiftGivingReportAudit) {
                if (this.hasPermissionsToAddComment) {
                    if (this.isGiftGivingReportInAreaAudit) {
                        return this.links.GIFT_GIVING_REPORT_AUDIT_LEADER_GUIDE;
                    }
                    return this.links.GIFT_GIVING_REPORT_AUDIT_SO_GUIDE;
                }
                return this.links.GIFT_GIVING_REPORT_AUDIT_VOLUNTEER_GUIDE;
            } else {
                if (this.hasPermissionsToAddComment) {
                    if (this.isFamilyInAreaAudit) {
                        return this.links.AUDIT_LEADER_GUIDE;
                    }
                    return this.links.AUDIT_SO_GUIDE;
                }
                return this.links.AUDIT_VOLUNTEER_GUIDE;
            }
        },
        toggleCardText () {
            return this.toggleCardFlag ? this.$t('audit.hide') : this.$t('audit.show');
        },
        xsOnly () {
            return this.$vuetify.breakpoint.xsOnly;
        },
        cookieSavePermission () {
            return (
                this.hasPermissionsForNationalCorrecting ||
        this.hasPermissionsForNationalGiftGivingReportCorrecting
            );
        },
        toggleCardVisible () {
            return this.toggleCardFlag ? 'block' : 'none';
        },
        toggleCardIcon () {
            return this.toggleCardFlag ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
        },
        toggleInstructionTitle () {
            return this.instructionVisibility
                ? this.$t('buttons.hide_instruction')
                : this.$t('buttons.show_instruction');
        },
        toggleInstructionIcon () {
            return this.instructionVisibility ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
        },
        instructionHeight () {
            if (!this.hasNationalPermission || this.instructionVisibility) {
                return 'auto';
            }
            if (!this.instructionVisibility) {
                return '21px';
            }
            return '0px';
        },
        showInstructionButton () {
            return (
                this.hasNationalPermission &&
        !(this.isFamilyInAreaAudit || this.isGiftGivingReportInAreaAudit)
            );
        },
        hasNationalPermission () {
            return (
                this.hasPermissionsForNationalCorrecting ||
        this.hasPermissionsForNationalGiftGivingReportCorrecting
            );
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        auditEndpoint () {
            const volunteerStatus = [
                this.statusesId.SEND_BACK_AREA_REACH,
                this.statusesId.SEND_BACK_NATIONAL_REACH,
                this.statusesId.GIFT_GIVING_REPORT_BACK_AREA_REACH,
                this.statusesId.GIFT_GIVING_REPORT_BACK_NATIONAL_REACH
            ];
            const areaStatus = [
                this.statusesId.FAMILY_VERIFIED_AREA_REACH,
                this.statusesId.GIFT_GIVING_REPORT_AREA_REACH
            ];
            const nationalStatus = [
                this.statusesId.FAMILY_VERIFIED_NATIONAL_REACH,
                this.statusesId.GIFT_GIVING_REPORT_NATIONAL_REACH,
                this.statusesId.PUBLICATION_REVOKED
            ];

            if (volunteerStatus.includes(this.familyStatusId)) {
                return this.isGiftGivingReportAudit
                    ? 'volunteer_gift_giving_report_audit_comment'
                    : 'volunteer_audit_comment';
            }
            if (areaStatus.includes(this.familyStatusId)) {
                return this.isGiftGivingReportAudit
                    ? 'area_gift_giving_report_audit_comment'
                    : 'area_audit_comment';
            }
            if (nationalStatus.includes(this.familyStatusId)) {
                return this.isGiftGivingReportAudit
                    ? 'national_gift_giving_report_audit_comment'
                    : 'national_audit_comment';
            }
            return 'national_gift_giving_report_audit_comment';
        },
        familyStatusId () {
            return this.family && this.family.status.family_edition_status_id;
        },
        isCommentInvalid () {
            return this.comment && (this.comment.content == null || this.comment.content === '');
        }
    },
    mounted () {
        this.updateCardWidth();
        window.onscroll = () => {
            this.updateAuditPadding();
        };
    },
    beforeDestroy () {
        window.onscroll = null;
    },
    async created () {
        if (this.$vuetify.breakpoint.xs) {
            this.auditCardFlag = false;
        }
        this.hasPermissionsForNationalCorrecting = await this.$store.dispatch(
            'auth/hasPermission',
            this.permissions.SHEET_CORRECTING_NATIONAL
        );
        this.hasPermissionsForNationalGiftGivingReportCorrecting = await this.$store.dispatch(
            'auth/hasPermission',
            this.permissions.GIFT_GIVING_REPORT_CORRECTING_NATIONAL
        );

        if (this.cookieSavePermission) {
            this.$store.dispatch('audit/checkForAuditExpand');
        }
        if (this.isFamilyInAreaAudit || this.isGiftGivingReportInAreaAudit) {
            this.instructionVisibility = true;
        }
    },
    methods: {
        toggleCard () {
            this.toggleCardFlag = !this.toggleCardFlag;
            this.$nextTick(() => {
                this.updateCardWidth();
            });
            this.$store.dispatch('audit/saveAuditExpand', {
                hasPermission: this.cookieSavePermission,
                isHidden: !this.toggleCardFlag
            });
        },
        toggleAuditCard () {
            this.auditCardFlag = !this.auditCardFlag;
            this.toggleCardFlag = false;
            this.$nextTick(() => {
                this.updateCardWidth();
            });
        },
        sendComment () {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.commentLoader = true;
                    this.comment.level = this.commentLevel;
                    const endpoint = this.auditEndpoint;
                    const _data = {
                        id: this.familyId,
                        sheetType: this.sheetType,
                        endpoint
                    };
                    this.$store
                        .dispatch('audit/sendAuditComment', {
                            isGift: this.isGiftGivingReportAudit,
                            params: _data
                        })
                        .then(({ data }) => {
                            this.$store.commit('audit/pushCommentToList', data.data);
                            this.$store.commit('audit/clearComment');
                            this.showCommentConfirmation = true;
                        })
                        .finally(() => {
                            this.commentLoader = false;
                        });
                }
            });
        },
        updateCardWidth () {
            this.updateAuditPadding();

            this.cardContentMaxHeight = this.cardMaxHeight - this.$refs.cardTitle.clientHeight;
            this.updateAuditPadding();

            const el = document.getElementById('sheet-container');
            if (el == null) return;

            const _width = el.offsetWidth - 50;
            if (this.$vuetify.breakpoint.smAndUp) {
                this.cardWidth = _width * 0.75;
            } else {
                this.cardWidth = _width;
            }
        },
        updateAuditPadding () {
            const auditElement = document.querySelector('.fixed-bottom.audit');
            if (auditElement) {
                let auditHeight = auditElement.clientHeight;

                if (this.$vuetify.breakpoint.xsOnly) {
                    auditHeight += 40;
                    document.querySelector('.mobile-audit-btn').style.bottom = auditHeight - 20 + 'px';
                }
                this.$refs.auditElement.style.paddingTop = auditHeight + 'px';
            }
        },
        toggleInstruction () {
            this.instructionVisibility = !this.instructionVisibility;
        }
    }
};
</script>

<style scoped></style>
